import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Send, Sparkles, Target, XCircle, RotateCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import { getSkillLabel } from '../data/skillAreas';
import CoachMarkdown from '../components/CoachMarkdown';

interface ChatMsg {
  role: 'user' | 'coach';
  content: string;
}

const EXAMPLES = [
  'Draft a difficult performance review for a struggling team member',
  'Plan a birthday party for my 8-year-old',
  'Summarize a long email thread into action items',
  'Write a wedding toast for my college roommate',
  'Prepare a Q4 strategy presentation for the executive team',
  'Create a weekly meal plan for a family of four',
];

export default function BringYourOwnProblemPage() {
  useDocumentMeta({
    title: 'Bring Your Own Problem — AI Coaching | PromptGPT',
    description: 'Describe a real task you need to get done. PromptGPT turns it into an interactive AI coaching session, teaching AI skills step by step. See exactly which skills you practiced at the end.',
    canonical: 'https://promptgpt.ai/practice',
  });

  const { user } = useAuth();
  const [taskInput, setTaskInput] = useState('');
  const [started, setStarted] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [coachTyping, setCoachTyping] = useState(false);
  const [stepCount, setStepCount] = useState(1);
  const [isComplete, setIsComplete] = useState(false);
  const [skillDeltas, setSkillDeltas] = useState<Record<string, number>>({});
  const [xpAwarded, setXpAwarded] = useState(0);
  const endRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bring-your-own-problem`;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const authHeaders = async (): Promise<Record<string, string>> => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      'apikey': anonKey || '',
      'Authorization': `Bearer ${session?.access_token || anonKey || ''}`,
    };
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, coachTyping]);

  const startSession = async () => {
    if (taskInput.trim().length < 5) {
      toast.error('Please describe your task in a bit more detail');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ action: 'start', taskDescription: taskInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'Could not start session');
        return;
      }
      setSessionId(data.sessionId);
      setMessages([{ role: 'coach', content: data.coachMessage }]);
      setStepCount(1);
      setStarted(true);
    } catch {
      toast.error('Could not start session');
    } finally {
      setSending(false);
    }
  };

  const sendResponse = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setCoachTyping(true);
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setInput('');
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) { ta.style.height = 'auto'; ta.style.height = Math.min(ta.scrollHeight, 220) + 'px'; }
    });

    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ action: 'continue', sessionId, userResponse: text }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'Could not continue session');
        setMessages((m) => m.slice(0, -1));
        return;
      }
      setCoachTyping(false);
      setMessages((m) => [...m, { role: 'coach', content: data.coachMessage }]);
      setStepCount((s) => s + 1);
      if (data.isComplete) {
        setIsComplete(true);
        setSkillDeltas(data.skillDeltas || {});
        setXpAwarded(data.xpAwarded || 0);
      }
    } catch {
      toast.error('Could not continue session');
      setMessages((m) => m.slice(0, -1));
    } finally {
      setSending(false);
      setCoachTyping(false);
    }
  };

  const endSessionEarly = async () => {
    if (messages.length < 2) {
      resetSession();
      return;
    }
    setCoachTyping(true);
    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ action: 'continue', sessionId, userResponse: 'I think I have what I need. Can you summarize what skills I practiced?' }),
      });
      const data = await res.json();
      setCoachTyping(false);
      if (res.ok) {
        setMessages((m) => [...m, { role: 'coach', content: data.coachMessage }]);
        if (data.isComplete) {
          setIsComplete(true);
          setSkillDeltas(data.skillDeltas || {});
          setXpAwarded(data.xpAwarded || 0);
        } else {
          setIsComplete(true);
          setSkillDeltas({ prompting: 3, reasoning: 3 });
        }
      } else {
        setIsComplete(true);
        setSkillDeltas({ prompting: 3, reasoning: 3 });
      }
    } catch {
      setCoachTyping(false);
      setIsComplete(true);
      setSkillDeltas({ prompting: 3, reasoning: 3 });
    }
  };

  const resetSession = () => {
    setStarted(false);
    setSessionId(null);
    setMessages([]);
    setInput('');
    setTaskInput('');
    setIsComplete(false);
    setSkillDeltas({});
    setXpAwarded(0);
    setStepCount(1);
    setCoachTyping(false);
  };

  if (!started) {
    return (
      <div className="container-app py-10 max-w-2xl">
        <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 mb-6">
          <ArrowLeft className="w-4 h-4" /> Today
        </Link>

        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">Bring Your Own Problem</div>
          <h1 className="font-display text-4xl sm:text-5xl mb-3 leading-tight">What are you trying to get done?</h1>
          <p className="text-ink-600 text-lg leading-relaxed">
            Tell us your real task. We will turn it into an interactive coaching session that teaches AI skills as you work through it.
          </p>
        </div>

        <div className="bg-white border border-ink-100 rounded-2xl p-6">
          <textarea
            value={taskInput}
            onChange={(e) => setTaskInput(e.target.value)}
            placeholder="e.g. I need to prepare a presentation for our Q4 strategy review."
            rows={4}
            className="w-full border border-ink-200 rounded-xl px-4 py-3 text-ink-800 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent transition"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                startSession();
              }
            }}
          />
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-ink-400">The coach will guide you step by step through your real task.</p>
            <button
              onClick={startSession}
              disabled={sending || taskInput.trim().length < 5}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? 'Starting…' : <>Start session <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          {EXAMPLES.map((example) => (
            <button
              key={example}
              onClick={() => setTaskInput(example)}
              className="text-left text-sm text-ink-600 hover:text-ink-900 bg-warm-50 border border-ink-100 rounded-xl p-3 transition hover:border-ink-200"
            >
              {example}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container-app py-8 max-w-3xl h-[calc(100vh-3.5rem)] lg:h-screen flex flex-col">
      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 mb-1">
            <ArrowLeft className="w-4 h-4" /> Today
          </Link>
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500">Bring Your Own Problem</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-ink-500 truncate max-w-xs italic">"{taskInput}"</div>
          {!isComplete && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink-400 font-medium">Step {stepCount}</span>
              <button
                onClick={endSessionEarly}
                className="text-xs text-ink-400 hover:text-coral-600 transition flex items-center gap-1"
                title="End session early"
              >
                <XCircle className="w-3.5 h-3.5" /> End
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 card p-5 sm:p-6 flex flex-col min-h-0">
        <div className="flex-1 overflow-y-auto scrollbar-thin pr-2 space-y-5">
          {messages.map((m, i) => (
            m.role === 'user' ? (
              <div key={i} className="flex gap-3 flex-row-reverse animate-fade-up">
                <div className="flex-shrink-0 grid place-items-center font-semibold w-8 h-8 rounded-full bg-ink-900 text-warm-50 text-sm">
                  {(user?.email || 'U').slice(0, 1).toUpperCase()}
                </div>
                <div className="max-w-[80%] p-4 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap bg-ink-900 text-warm-50">
                  {m.content}
                </div>
              </div>
            ) : (
              <div key={i} className="flex gap-3 animate-fade-up">
                <div className="flex-shrink-0 grid place-items-center w-8 h-8 rounded-full bg-accent-100 text-accent-700">
                  <Target className="w-4 h-4" />
                </div>
                <div className="flex-1 rounded-2xl border border-ink-100 bg-warm-50 p-5">
                  <CoachMarkdown text={m.content} variant="default" />
                </div>
              </div>
            )
          ))}

          {/* Typing indicator */}
          {coachTyping && (
            <div className="flex gap-3">
              <div className="flex-shrink-0 grid place-items-center w-8 h-8 rounded-full bg-accent-100 text-accent-700">
                <Target className="w-4 h-4" />
              </div>
              <div className="rounded-2xl border border-ink-100 bg-warm-50 p-4 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-ink-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full bg-ink-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 rounded-full bg-ink-300 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {isComplete ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-5 border-t border-ink-100 pt-5"
          >
            <div className="bg-accent-50 border border-accent-200 rounded-2xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-accent-600" />
                <span className="font-display text-lg">Session complete</span>
                {xpAwarded > 0 && (
                  <span className="ml-auto text-accent-700 font-bold text-sm">+{xpAwarded} XP</span>
                )}
              </div>
              <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-2">Skills you practiced</div>
              <div className="space-y-2">
                {Object.entries(skillDeltas).map(([area, delta]) => (
                  <div key={area} className="flex items-center justify-between">
                    <span className="text-sm text-ink-700">{getSkillLabel(area)}</span>
                    <span className="text-sm font-semibold text-mint-600">+{delta}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={resetSession} className="btn-primary flex-1 justify-center">
                <RotateCw className="w-4 h-4" /> Start a new problem
              </button>
              <Link to="/learn" className="btn-secondary flex-1 justify-center">Back to Today</Link>
            </div>
          </motion.div>
        ) : (
          <form
            onSubmit={(e) => { e.preventDefault(); sendResponse(); }}
            className="mt-5 flex gap-3 items-end"
          >
            <textarea
              ref={textareaRef}
              className="textarea flex-1 min-h-[52px]"
              placeholder="Type your response…"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                const ta = e.target;
                ta.style.height = 'auto';
                ta.style.height = Math.min(ta.scrollHeight, 220) + 'px';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendResponse();
                }
              }}
              rows={1}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="btn-primary disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        )}
        {!isComplete && (
          <div className="mt-2 text-[11px] text-ink-400">Press Enter to send · Shift+Enter for a new line</div>
        )}
      </div>
    </div>
  );
}
